// orderService.js
// Order submission is idempotent + atomic:
// - The cart generates a unique order ID before submitting; retries reuse the
//   same ID, so a network hiccup / double-submit can never create two orders.
// - Order creation and stock deduction happen in ONE Firestore transaction,
//   so stock can never be deducted twice, and an order can never exist
//   without its stock deduction (or vice versa).
//
// Backorders: customers may order MORE than the stock level (no hard cap in
// the UI as long as the item is in stock at all). Stock floors at 0, and each
// line item records deductedQty — the amount actually taken from stock — so
// cancellation restores exactly what was deducted, never phantom stock.
//
// Sync lock: when the QB sync server picks an order up for a Web Connector
// session it stamps qbSyncStarted + syncStartedAt (see C:\SunjinQBSync\
// syncServer.js). From that moment the order cannot be cancelled — the UI
// grays out Edit/Cancel and cancelOrder rejects it here too, inside the
// transaction, so a stale page can't slip a cancel through. Any changes after
// that point are handled manually in QuickBooks (call Sunjin). Locks older
// than 30 minutes are treated as stale (crashed-sync failsafe).

import { db } from "../config/firebase";
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { isPromoActive, isPromoExpired, effectivePrice } from "../utils/promo";
import { specialPriceFor } from "./customerPricingService";
import { invalidateProductCache } from "./dataCache";

const SYNC_LOCK_STALE_MS = 30 * 60 * 1000;

/**
 * True while an order is locked by an in-flight QB sync session.
 * Only meaningful for pending_sync orders: success flips them to synced
 * (uncancellable anyway) and failure clears the lock with status error.
 */
export function isSyncLocked(order) {
  if (!order || order.status !== "pending_sync" || !order.qbSyncStarted) return false;
  const startedMs = order.syncStartedAt?.toMillis?.() ?? 0;
  return Date.now() - startedMs < SYNC_LOCK_STALE_MS;
}

// customerId: portal account the order belongs to (null for orders placed on
//   behalf of a customer RECORD with no linked account).
// customerRecordId: the customers-collection record for rep/admin
//   order-on-behalf; the sync server resolves the QB customer name from the
//   record (live at sync time) when this is set.
// placedBy: who actually placed it (rep/admin uid for on-behalf orders).
// comment: optional one-time note from the checkout box (NOT a chat thread);
//   badge on admin Orders, and the sync server emails Brian about it.
// keepWrittenOrder: set for paste-imported orders (handwritten POs) — the
// sync server then keeps the QB invoice lines in AS-WRITTEN order so the
// store can check the invoice against their own PO copy. The packing sheet
// still sorts by storage for the warehouse (it sorts independently).
export async function submitOrder({ orderId, customerId, placedBy, items, customerRecordId = null, comment = "", keepWrittenOrder = false }) {
  const orderNumber = `SJ-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`;

  const orderRef = doc(db, "orders", orderId);

  const result = await runTransaction(db, async (transaction) => {
    // 1. If this exact order was already placed (retry after network error),
    //    return the existing one instead of creating a duplicate.
    const existing = await transaction.get(orderRef);
    if (existing.exists()) {
      return { orderNumber: existing.data().orderNumber, duplicate: true };
    }

    // 2. Read all product docs first (Firestore requires all reads before writes)
    const productReads = [];
    for (const item of items) {
      const pRef = doc(db, "products", item.sku);
      productReads.push({ item, ref: pRef, snap: await transaction.get(pRef) });
    }
    // Store-specific special pricing (2026-08-24): read the ordering store's
    // pricing doc LIVE in the same transaction — expired/toggled-off entries
    // can't leak from a stale cart. Store price WINS over a global promo
    // (Brian's rule); staff deal-price overrides still beat everything.
    let storePricing = null;
    if (customerRecordId) {
      const priceSnap = await transaction.get(doc(db, "customerPricing", customerRecordId));
      storePricing = priceSnap.exists() ? priceSnap.data() : null;
    }

    // 3. Enrich each line from LIVE product state:
    //    - priceAtOrder is recomputed here (promo price while the promo is
    //      active, base price otherwise) so a promo that expired while the
    //      item sat in the cart can't leak a stale discount — the transaction
    //      is the price authority, not the cart display.
    //    - deductedQty records how much stock this order actually consumes.
    //      Ordering 10 with 3 in stock deducts 3, the other 7 are backordered
    //      — cancelling later restores only the 3.
    const enrichedItems = productReads.map(({ item, snap }) => {
      const data = snap.exists() ? snap.data() : null;
      const available = data ? Math.max(0, data.stockQty || 0) : 0;
      const promoActive = data ? isPromoActive(data) : false;
      // Store special price (active entries only). Applies to normal AND
      // return lines — the store's price IS their price; credits and staff
      // custom prices are exempt.
      const special = (!item.customPrice && !item.creditLine)
        ? specialPriceFor(storePricing, item.sku) : null;
      // Credit lines (item 10000) keep their client-set negative price —
      // they're not products, so live repricing must not touch them.
      // Return lines (negative qty) restore their FULL quantity to stock.
      const deductedQty = item.creditLine ? 0
        : item.qty >= 0 ? Math.min(item.qty, available)
        : item.qty;
      return {
        ...item,
        // customPrice = staff override / credit / free-goods $0 line — keep
        // the client-set price; then store special price; then promo/base.
        priceAtOrder: (item.customPrice || item.creditLine) ? item.priceAtOrder
          : special != null ? special
          : data ? effectivePrice(data) : item.priceAtOrder,
        // Flag rides to the sync server -> QB Desc "special promotion".
        ...(special != null ? { specialPrice: true } : {}),
        deductedQty,
        // How many units this line counted toward the promo case limit —
        // cancellation gives exactly this back (if the promo still runs).
        // Returns/credits/custom-priced/store-priced lines never consume
        // the GLOBAL promo allocation.
        promoQtyCounted: promoActive && item.qty > 0 && !item.customPrice && special == null ? item.qty : 0
      };
    });
    const totalAmount = enrichedItems.reduce((sum, i) => sum + i.qty * i.priceAtOrder, 0);

    // 4. Create the order. Commented orders carry commentEmailSent: false —
    //    the sync server listens for that flag and emails Brian, then flips
    //    it true (comments-only notifications, settled 2026-07-10).
    const trimmedComment = (comment || "").trim();
    transaction.set(orderRef, {
      orderNumber,
      customerId: customerId || null,
      customerRecordId: customerRecordId || null,
      placedBy,
      items: enrichedItems,
      totalAmount,
      comment: trimmedComment || null,
      ...(trimmedComment ? { commentEmailSent: false } : {}),
      // Confirmation email goes to the order's account holder (sync server
      // listener); orders for unlinked customer RECORDS have no email — skip.
      ...(customerId ? { confirmEmailSent: false } : {}),
      ...(keepWrittenOrder ? { keepWrittenOrder: true } : {}),
      status: "pending_sync",
      syncedAt: null,
      createdAt: serverTimestamp()
    });

    // 5. Deduct stock in the same transaction (floors at 0 on backorder),
    //    and count promo-priced sales toward the promo's case limit. The
    //    last promo order may overshoot the limit (whole line keeps the promo
    //    price) — accepted trade-off vs. splitting a line into two prices.
    for (const { ref, snap, item } of productReads) {
      if (!snap.exists() || item.creditLine) continue; // credit isn't stock
      const data = snap.data();
      // Negative qty (a return) ADDS stock back; positive deducts, floor 0.
      const update = { stockQty: Math.max(0, (data.stockQty || 0) - item.qty) };
      if (isPromoActive(data) && item.qty > 0) {
        update.promoSoldQty = (data.promoSoldQty || 0) + item.qty;
      }
      transaction.update(ref, update);
    }

    return { orderNumber, duplicate: false };
  });

  invalidateProductCache(); // stock/promo counters changed
  return result;
}

/**
 * Cancels a pending order and restores its stock, atomically.
 * Only pending_sync or error orders can be cancelled (synced ones live in QB),
 * and NOT while a QB sync session has the order locked (see isSyncLocked) —
 * its invoice may already be in flight to QuickBooks.
 * Restores each line's deductedQty (what was actually taken from stock), NOT
 * the ordered qty — a backordered line deducted less than it ordered.
 * Orders placed before deductedQty existed fall back to qty, which is exact
 * for them because over-stock ordering wasn't possible then.
 */
export async function cancelOrder(orderId) {
  await runTransaction(db, async (transaction) => {
    const orderRef = doc(db, "orders", orderId);
    const orderSnap = await transaction.get(orderRef);
    if (!orderSnap.exists()) throw new Error("Order not found.");
    const order = orderSnap.data();
    if (order.status !== "pending_sync" && order.status !== "error") {
      throw new Error("This order is already in QuickBooks and can only be changed there.");
    }
    if (isSyncLocked(order)) {
      throw new Error("This order is being sent to QuickBooks right now and can no longer be cancelled here. Please contact Sunjin Trading for any changes.");
    }

    // Read all products first (reads before writes)
    const productReads = [];
    for (const item of order.items || []) {
      const pRef = doc(db, "products", item.sku);
      productReads.push({
        item,
        ref: pRef,
        snap: await transaction.get(pRef),
        restoreQty: item.deductedQty ?? item.qty
      });
    }

    transaction.update(orderRef, { status: "cancelled" });

    for (const { item, ref, snap, restoreQty } of productReads) {
      if (!snap.exists()) continue;
      const data = snap.data();
      const update = { stockQty: (data.stockQty || 0) + restoreQty };
      // Give promo allocation back (Brian, 2026-07-10) — but ONLY while the
      // SAME promo is still running: not after its end date, not after the
      // admin ended it, and not if a different promo replaced it (price
      // match guards that). A sold-out promo DOES get its allocation back —
      // that's the point: a cancel can re-open the last cases.
      if ((item.promoQtyCounted || 0) > 0
          && data.promoPrice != null
          && !isPromoExpired(data)
          && item.priceAtOrder === data.promoPrice) {
        update.promoSoldQty = Math.max(0, (data.promoSoldQty || 0) - item.promoQtyCounted);
      }
      transaction.update(ref, update);
    }
  });
  invalidateProductCache(); // stock restored / promo allocation returned
}

/**
 * Admin: re-queue an errored order for the next QB sync run.
 * Transactional status check: only an "error" order may be re-queued. Without
 * it, a stale Orders page on another machine could flip a synced order back
 * to pending_sync (duplicate QB invoice) or re-queue a cancelled order whose
 * stock was already restored.
 * Stock is NOT touched here — it was deducted once when the order was placed
 * and a failed sync never restores it, so there is nothing to re-deduct.
 */
export async function retryOrder(orderId) {
  await runTransaction(db, async (transaction) => {
    const orderRef = doc(db, "orders", orderId);
    const orderSnap = await transaction.get(orderRef);
    if (!orderSnap.exists()) throw new Error("Order not found.");
    const status = orderSnap.data().status;
    if (status !== "error") {
      if (status === "synced") throw new Error("This order is already in QuickBooks — retrying would create a duplicate invoice.");
      if (status === "pending_sync") throw new Error("This order is already waiting for the next QuickBooks sync.");
      if (status === "cancelled") throw new Error("This order was cancelled and cannot be retried.");
      throw new Error(`This order cannot be retried (status: ${status}).`);
    }
    transaction.update(orderRef, { status: "pending_sync", syncError: null });
  });
}
